<template>
  <v-card
    :id="props.data.id"
    ref="practitionerCard"
    color="white"
    class="d-flex flex-md-column flex-column-reverse"
    elevation="0"
    border="sm"
  >
    <div>
      <v-card-title
        class="py-2 justify-center text-center d-flex w-full align-center text-subtitle-1 font-weight-light bg-surface-lighten-1 text-primary tagline"
        :class="{ title: smAndDown }"
      >
        <template v-if="props.data.tagline">
          “{{ props.data.tagline }}”
        </template>
      </v-card-title>
    </div>
    <v-card-text class="pb-0">
      <div
        class="d-flex align-center justify-space-evenly ga-3"
        :class="{ 'flex-column': smAndDown }"
      >
        <!-- Start left part -->
        <div
          class="left-part d-flex flex-column ga-1 flex-b-fit pt-4 py-md-4 text-primary"
        >
          <div class="d-flex pract-card">
            <div class="d-flex avatar-part">
              <!-- Avatar + name -->
              <div class="avatar-name d-flex ga-2">
                <div class="avatar relative">
                  <v-avatar
                    :size="88"
                    class="bg-grey cursor-pointer avatar-circle"
                    @click="onTitleClick"
                  >
                    <v-img
                      v-if="props.data.avatarUrl"
                      :src="props.data.avatarUrl"
                      :alt="`${props.data.practitionerName} avatar`"
                      class="avatar-img"
                    />
                    <PractitionerAvatar
                      v-if="!props.data.avatarUrl"
                      color="#fff"
                    />
                    <div
                      v-if="props.data.remoteAllowed"
                      class="video-icon elevation-2"
                    >
                      <v-icon color="blue" size="20">mdi-video</v-icon>
                    </div>
                  </v-avatar>

                  <div
                    v-if="props.data.priceFrom"
                    class="from-price-duration d-flex align-center mt-2"
                    :class="{ 'mx-auto': smAndDown }"
                  >
                    <span
                      class="text-center"
                      v-html="
                        $t('practitionerPublicProfile.fromPrice', {
                          price: props.data.priceFrom,
                        })
                      "
                    ></span>
                  </div>
                </div>
              </div>
            </div>
            <div class="d-flex flex-column pract-content">
              <div
                class="name-discipline ml-2 mb-1 d-flex flex-column mobile-center"
              >
                <div class="d-flex align-center text-wrap">
                  <h3
                    class="text-h5 mr-3 text-primary font-weight-bold cursor-pointer practitioner-name"
                    @click="onTitleClick"
                  >
                    {{ props.data.practitionerName }}
                  </h3>
                </div>
                <VerifiedBadge
                  class="mt-1"
                  :is-verified="props.data.isVerified"
                  type="green"
                />
                <div class="d-flex">
                  <h4
                    class="my-2 text-secondary main-discipline"
                    v-html="displayDiscipline"
                  ></h4>
                </div>
              </div>

              <div class="d-flex">
                <div
                  v-if="props.data.durationMin && props.data.durationMax"
                  class="pr-5 d-flex align-center w-100"
                >
                  <v-icon class="mr-1 text-secondary"
                    >mdi-clock-time-four-outline</v-icon
                  >
                  <span v-if="props.data.durationMin < props.data.durationMax">
                    {{ formatTime(props.data.durationMin) }} -
                    {{ formatTime(props.data.durationMax) }}
                  </span>
                  <span v-else class="card-item-label">{{
                    formatTime(props.data.durationMax)
                  }}</span>
                </div>
                <!-- <div
                  v-if="props.data.durationMin && props.data.durationMax"
                  class="pr-5 d-flex align-center"
                >
                  <v-icon class="mr-1 text-secondary">mdi-video-outline</v-icon>
                  <span v-if="props.data.remoteAllowed">
                    Téléconsultation
                  </span>
                  <span v-else class="card-item-label">Présentiel</span>
                </div> -->
              </div>
              <div class="d-flex flex-column mt-2">
                <v-select
                  v-model="props.data.address"
                  :items="
                    props.data.addresses.filter(
                      (address, index, self) =>
                        index === self.findIndex(a => a.id === address.id),
                    )
                  "
                  :auto-select-first="true"
                  item-title="formattedAddress"
                  item-value="location"
                  return-object
                  density="compact"
                  hide-details
                  mandatory
                  validate-on="lazy"
                  @update:model-value="() => fetchChildAppointments()"
                  class="mt-2 address"
                  flat
                >
                  <template v-slot:prepend-inner>
                    <v-icon color="primary">mdi-map-marker-outline</v-icon>
                  </template>
                </v-select>
                <span
                  v-if="!props.data.address.remote"
                  class="text-primary mt-1 mb-2 opacity-60"
                  :class="{ 'ml-4': smAndDown }"
                  >{{
                    props.data?.address?.distanceKm
                      ? $t('practitionerPublicProfile.distanceFromPatient', {
                          distance: convertirMetres(
                            props.data.address.distanceKm,
                          ),
                        })
                      : ''
                  }}</span
                >
              </div>
              <div class="d-flex my-1">
                <div class="card-item-label me-1">
                  {{ $t('audience') }} :
                  <span class="card-item-content">{{
                    props.data.audience
                  }}</span>
                </div>
              </div>
              <div class="d-flex my-1" v-if="!smAndDown">
                <div class="card-item-label me-1">
                  {{ $t('profile.payment') }} :
                  <span class="card-item-content">{{
                    props.data.paymentMethods
                  }}</span>
                </div>
              </div>

              <div class="d-flex my-1">
                <div class="card-item-label me-1">
                  {{ $t('searchPage.spokenLanguages.label') }} :
                  <span class="card-item-content">{{
                    props.data.languages
                  }}</span>
                </div>
              </div>

              <div
                class="d-flex my-1 profil-avis"
                v-if="props.data.opinionsPositive > 0"
              >
                <div class="card-item-label me-1">
                  <span class="card-item-content text-secondary">
                    <v-icon color="secondary" class="card-item-icon"
                      >mdi-thumb-up-outline</v-icon
                    >
                    {{ props.data.opinionsPositive }}</span
                  >
                  <span class="ps-1" v-if="props.data.opinionsPositive == 1">
                    {{ $t('searchPage.opinionPositive') }}
                  </span>
                  <span class="ps-1" v-else>
                    {{ $t('searchPage.opinionsPositive') }}
                  </span>
                </div>
              </div>
            </div>
          </div>
          <!-- end left part -->
        </div>

        <div
          v-if="!(smAndDown && props.data.nextAppointment)"
          class="divider mb-n4"
          :class="{ 'divider-xs mx-0': smAndDown, 'mx-4': !smAndDown }"
        ></div>
        <!-- start right part -->
        <div>
          <MeetingSlotsPractitioner
            ref="MeetingSlotsPractitionerRef"
            :next-appointment="props.data.nextAppointment"
            :practitioner-data="props.data"
          />
        </div>
      </div>
    </v-card-text>

    <v-card-title
      class="py-2 d-flex w-full align-center justify-center font-weight-light tagline-bottom bg-white"
      :class="{ title: smAndDown }"
    >
      <div class="mobile-discipline flex-column">
        <div
          class="d-flex align-center ga-2 flex-wrap mb-2 align-self-baseline"
        >
          <v-chip
            v-for="discipline in props.data.disciplines"
            :key="props.data.id + '-' + discipline"
            color="secondary"
            density="compact"
            class="chip-custom text-break"
          >
            {{ discipline.label }}
          </v-chip>
          <!-- </div>

        <div class="d-flex align-center ga-2 flex-wrap"> -->
          <!-- Services avec gestion des "..." -->
          <template v-if="!showAllServices">
            <v-chip
              v-for="service in limitedServices"
              :key="props.data.id + '-' + service"
              color="primary"
              class="opacity-60 text-break"
              density="compact"
            >
              {{ service }}
            </v-chip>
            <v-chip
              v-if="props.data.services.length > 1"
              color="primary"
              class="opacity-60 cursor-pointer"
              density="compact"
              @click="showAllServices = true"
            >
              <v-icon>mdi-dots-horizontal</v-icon>
            </v-chip>
          </template>

          <template v-else>
            <v-chip
              v-for="service in props.data.services"
              :key="props.data.id + '-' + service"
              color="primary"
              class="opacity-60"
              density="comfortable"
            >
              {{ service }}
            </v-chip>
          </template>
        </div>

        <ReviewPercentage
          :count-reviews="props.data.reviews"
          :review-percentage="props.data.reviewPercentage"
        />
      </div>
    </v-card-title>
  </v-card>
</template>
<script lang="ts" setup>
import { IPractitionerCard } from '@/store/search-page/mainSearchBarStore.d'
import { PropType, ref, watch, computed } from 'vue'
import ReviewPercentage from '../common/ReviewPercentage.vue'
import MeetingSlotsPractitioner from './MeetingSlotsPractitioner.vue'
import DefaultAvatar from '@/components/DefaultAvatar.vue'
import { useDisplay } from 'vuetify'
import { useIntersectionObserver } from '@vueuse/core'
import { useMainSearchBarStore } from '@/store/search-page/mainSearchBarStore'
import { goToProfile } from './SearchPageHelper'
import { formatTime } from '@/utils/formatTime'
import PractitionerAvatar from '@/components/PractitionerAvatar.vue'
import VerifiedBadge from '../practitionerProfile/VerifiedBadge.vue'
import { convertirMetres } from '@/utils/convertirMetres'

const props = defineProps({
  data: Object as PropType<IPractitionerCard>,
  isInThreeLast: Boolean,
})
const MeetingSlotsPractitionerRef = ref(null)
function fetchChildAppointments() {
  if (MeetingSlotsPractitionerRef.value) {
    MeetingSlotsPractitionerRef.value.fetchAvailabilities()
  }
}

const { smAndDown } = useDisplay()

const isVisible = ref(false)
const practitionerCard = ref(null)

useIntersectionObserver(practitionerCard, ([{ isIntersecting }]) => {
  isVisible.value = isIntersecting
})

const mainSearchBarStore = useMainSearchBarStore()

watch(isVisible, async (newVal, _) => {
  if (
    newVal &&
    props.isInThreeLast &&
    mainSearchBarStore.pagination.hasMorePage &&
    !mainSearchBarStore.loadingFind
  ) {
    // fetch next page on last item visible
    mainSearchBarStore.pagination.page++
    await mainSearchBarStore.findYourPractitioner(true)
  }
})

const onTitleClick = async () => {
  const nextAvailability = props.data.nextAppointment ?? ''

  await goToProfile(
    nextAvailability,
    props.data.address,
    props.data.mainDiscipline.slug,
    props.data.address.city,
    props.data.slug, // slug practitioner
    false,
    '',
    props.data.address.remote ? 'remote' : 'in-person',
  )
}

const showAllServices = ref(false)
const limitedServices = computed(() => {
  return props.data.services.slice(0, 1)
})

const displayDiscipline = computed(() => {
  const matchedDiscipline = props.data.highlight?.disciplines?.find(highlight =>
    highlight.label.matched_tokens.some(token =>
      props.data.disciplines.some(discipline => discipline.label === token),
    ),
  )

  return matchedDiscipline?.label.snippet || props.data.mainDiscipline.label
})
</script>
<style scoped lang="scss">
.tagline {
  min-height: 48px;
  color: #007058 !important;
  font-weight: 400 !important;
  background-color: #e3f3e8;
}
.title {
  font-size: clamp(12px, 4vw, 22px);
  white-space: initial;
}
.text-wrap {
  overflow-wrap: break-word;
  word-break: break-all;
}
.chip-custom {
  overflow-wrap: break-word;
  word-break: break-all;
  white-space: normal; /* Allow text to wrap */
  height: fit-content;
  padding: 2px 12px;
}
.relative {
  position: relative;
}
.left-part {
  flex: 0 0 46%;
  width: 80%;
}
.avatar {
  width: fit-content;
  height: fit-content;
}
.video-icon {
  position: absolute;
  bottom: 0;
  right: 0;
  background-color: #fff;
  border-radius: 100%;
  width: 30px;
  height: 30px;
  display: flex;
  justify-content: center;
  align-items: center;
}
.line-height-1 {
  line-height: 1;
}
.from-price-duration {
  width: fit-content;
}
.divider {
  width: 1px;
  background-color: #cecece99;
  align-self: stretch;
  flex: 0 0 1px;
}
.divider {
  width: 100%;
}

/* Style par défaut */
.name-discipline {
  display: flex;
  flex-direction: column;
}

.avatar-name .text-h5 {
  word-break: auto-phrase;
}

.avatar-circle {
  border: 3px solid #12c29b;
  width: 100px !important;
  height: 100px !important;
  overflow: visible;
}

.practitioner-name {
  color: #004838 !important;
  font-family: 'Poppins';
  word-wrap: break-word;
  word-break: break-word;
}

.main-discipline {
  font-family: 'Poppins';
  font-size: 16px;
  font-weight: 600;
}

.card-item-label {
  font-family: 'Poppins';
  font-size: 14px;
  line-height: 21px;
  font-weight: 400 !important;
  color: #616161;
}

.card-item-content {
  font-family: 'Poppins';
  font-size: 14px;
  line-height: 21px;
  font-weight: 600;
  color: #616161;
}

.from-price-duration {
  color: #029978;
  font-family: 'Poppins';
  font-size: 19px;
  font-weight: 700;
  line-height: 26.6px;
}

.gap-2 {
  gap: 20px;
}

.tagline-bottom {
  border-top: 1px solid #e3f3e8;
}

.avatar-part {
  width: 20%;
}

.pract-content {
  padding-left: 25px;
  width: 80%;
}

.pract-card {
  gap: 20px;
}

.no-border .v-input {
  border: none !important;
  box-shadow: none !important;
}

.address .v-field__prepend-inner > .v-icon {
  color: #12c29b !important;
  opacity: 1;
}

/* Styles spécifiques pour les mobiles */
@media (max-width: 768px) {
  .avatar-name {
    flex-direction: column;
    align-items: center;
  }

  .mobile-center {
    align-items: center;
    text-align: center;
  }

  .mobile-discipline {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    margin-top: 4px;
    margin-bottom: 4px;
  }

  .avatar {
    align-items: center;
    align-content: center;
    align-self: center;
    justify-content: center;
    display: flex;
    flex-direction: column;
  }

  .pract-card {
    display: flex;
    flex-direction: column;
    gap: 5px;
  }

  .avatar-part {
    width: 100%;
    justify-content: center;
  }

  .pract-content {
    padding-left: 0px;
    width: 100%;
    justify-content: center;
  }

  .chip-custom {
    max-width: 200px; /* Limit the width */
  }
}
.avatar-img {
  border-radius: 60px;
}

.profil-avis {
  border: 1px solid #ededed;
  padding: 2px;
  padding-inline: 18px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  margin-top: 15px !important;
}

.card-item-icon {
  font-size: 16px;
  padding-bottom: 1.5px;
}

::v-deep(mark) {
  background-color: #eaf3ff !important;
  padding-right: 10px;
  color: #029978;
  font-weight: 600;
}
</style>
